import { later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';

import type { EventData, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import type { TourId } from 'webapp/enums/tour';
import { getTourDoneKeyFromTourId } from 'webapp/enums/tour';

import PopupListComponent from 'webapp/components/common/popups/popup-list';

import type Tour from 'webapp/models-custom/tour';

// Flow/Tour only work with tours that are linear (exact order),
// have only one step for each tour,
// and certain steps can be finished before it's their turn

export default abstract class FlowTourBaseService extends EmberService {
	@service('popup') declare popupService: Service.Popup;
	@service('tour') declare tourService: Service.Tour;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	activeTour: Tour | null = null;

	isAllFinished = false;

	abstract get orderedTourIds(): TourId[];

	private _isTemporaryBlockerActive = false; // temporarily stop any new tour from starting

	private isFlowActivated = false;

	private allowedOpenPopups = [PopupListComponent];

	private nextTourTimeout = 700;

	get isTemporaryBlockerActive(): boolean {
		return this._isTemporaryBlockerActive;
	}

	set isTemporaryBlockerActive(value: boolean) {
		this._isTemporaryBlockerActive = value;
	}

	get areAnyConflictingPopupsOpen(): boolean {
		return this.popupService.popupsActive.some(
			(popup) =>
				!this.allowedOpenPopups.some((allowed) => popup.componentInnerClass === allowed),
		);
	}

	checkTour(): void {
		later(() => {
			if (!this.isFlowActivated) {
				// only do this once
				this.activate();
				this.isFlowActivated = true;
			}
			if (!this.checkGeneralEligibility()) {
				return;
			}
			const nextEligibleId = this.getNextEligibleTour();
			if (nextEligibleId) {
				this.triggerTourEvent(nextEligibleId);
			}
		}, this.nextTourTimeout);
	}

	startTour(tourId: TourId, step?: number, opts: Record<string, unknown> = {}): Tour | null {
		const tour = this.tourService.initTour(tourId, opts);
		if (tour) {
			this.activeTour = tour;
			tour.start(step);
		}
		return tour;
	}

	endTour(tourId: TourId): void {
		// this will NOT always be called when popups are "just closed"
		// cleanup of activeTour will take place is allPopupsClosed callback
		if (this.activeTour?.id === tourId) {
			this.activeTour?.end();
			this.activeTour = null;
		} else if (this.isTourEligible(tourId)) {
			const tour = this.tourService.initTour(tourId);
			if (tour) {
				tour.markDone();
				tour.end();
			}
		}
	}

	bindTourCheck(tourId: TourId, context: object, callback: () => void): void {
		if (this.isAllFinished) {
			return;
		}
		const eventName = this.getEventNameFromTourId(tourId);
		this.eventManagerService.on(eventName, context, callback);
	}

	unbindTourCheck(tourId: TourId, context: object): void {
		const eventName = this.getEventNameFromTourId(tourId);
		this.eventManagerService.off(eventName, context);
	}

	checkGeneralEligibility(): boolean {
		return (
			!this.isAllFinished &&
			!this.isTemporaryBlockerActive &&
			!this.activeTour?.isActive &&
			!this.areAnyConflictingPopupsOpen &&
			!this.currentUserService.loggedInAsPowerUser
		);
	}

	endAllTours(): void {
		this.orderedTourIds.forEach((tourId) => {
			this.endTour(tourId);
		});
		this.deactivate();
	}

	private getNextEligibleTour(): TourId | null {
		const nextEligibleId = this.orderedTourIds.find((tourId) => {
			return this.isTourEligible(tourId);
		});
		if (!nextEligibleId) {
			this.deactivate();
			return null;
		}
		return nextEligibleId;
	}

	private activate(): void {
		this.eventManagerService.on(AppEvent.ALL_POPUPS_CLOSED, this, () => {
			// explicitly call end on activeTour
			// bcos not everywhere tours get properly de-activated
			const activeTour = this.activeTour;
			if (activeTour) {
				activeTour.end();
				this.activeTour = null;
			}
			this.checkTour();
		});
	}

	private deactivate(): void {
		this.isAllFinished = true;
		this.eventManagerService.off(AppEvent.ALL_POPUPS_CLOSED, this);
	}

	private triggerTourEvent(tourId: TourId): void {
		const eventName = this.getEventNameFromTourId(tourId);
		this.eventManagerService.trigger(eventName);
	}

	private isTourEligible(tourId: TourId): boolean {
		const persistantKey = getTourDoneKeyFromTourId(tourId);
		return this.tourService.isTourEligible(persistantKey);
	}

	private getEventNameFromTourId(tourId: TourId): EventData.TourName {
		return `tour__${tourId}`;
	}
}
